package com.evoapps.evoshift

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.open_sans
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.configureWebResources
import org.jetbrains.compose.resources.preloadFont
import androidx.compose.ui.window.ComposeViewport
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.remote.FirebaseProject
import com.evoapps.evoshift.data.repository.AvailabilityLinkRepositoryImpl
import com.evoapps.evoshift.appcheck.WebAppCheck
import com.evoapps.evoshift.ui.AppTheme
import com.evoapps.evoshift.ui.availabilitylink.AvailabilityLinkScreen
import com.evoapps.evoshift.viewmodels.AvailabilityLinkViewModel
import io.ktor.client.HttpClient
import com.evoapps.evoshift.navigation.DeepLinks
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseOptions
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.FirebaseFirestoreSettings
import dev.gitlive.firebase.firestore.LocalCacheSettings
import dev.gitlive.firebase.firestore.firestoreSettings
import dev.gitlive.firebase.initialize
import kotlinx.browser.window
import org.jetbrains.skiko.wasm.onWasmReady
import org.w3c.dom.events.WheelEvent
import org.w3c.dom.url.URLSearchParams

/** Firestore/Auth emulator ports from firebase.json (distinct from EVoLabels' defaults). */
private const val EMULATOR_FIRESTORE_PORT = 8180
private const val EMULATOR_AUTH_PORT = 9199
private const val EMULATOR_FUNCTIONS_PORT = 5101
private const val EMULATOR_STORAGE_PORT = 9399

/**
 * An emulator port given in the URL (`?emulator&firestorePort=8290`), else the dev one: the web
 * smoke test (e2e/web, firebase.e2e-web.json) runs its own emulators beside the dev ones.
 */
private fun emulatorPort(name: String, default: Int): Int =
    URLSearchParams(window.location.search).get(name)?.toIntOrNull() ?: default

@OptIn(ExperimentalComposeUiApi::class, ExperimentalResourceApi::class)
fun main() {
    // SH-189: every resource URL carries this build's id. The bundle reads strings by byte offset
    // inside the .cvr files, so it must never get the files of another release from a cache.
    // `<base href="/">` in the pages makes "./composeResources/..." absolute (/web/..., /join, /d/...).
    configureWebResources {
        resourcePathMapping { path -> "./$path?v=${WebBuild.ID}" }
    }
    // SH-240: the campaign labels of the address the app was opened with, before it navigates.
    WebSignupSource.capture(window.location.search)
    keepSidewaysWheelInTheApp()
    onWasmReady {
        // SH-173: an e-mailed availability link (/d/<token>, availability-link.html): only that
        // form, without the app, Firebase or a sign-in.
        val path = window.location.pathname
        if (path.startsWith(AVAILABILITY_LINK_PATH)) {
            showAvailabilityLinkPage(path.removePrefix(AVAILABILITY_LINK_PATH).substringBefore('/'))
            return@onWasmReady
        }

        // Local development against the Firebase emulators: open the app with `?emulator`
        // (docs/testing.md). Only honoured on localhost, never on the hosted site. It switches to
        // a `demo-` project, so nothing local can ever reach the real one.
        val host = window.location.hostname
        val useEmulators = window.location.search.contains("emulator") && (host == "localhost" || host == "127.0.0.1")

        // Firebase web client config (FirebaseProject: shared with the desktop app, SH-97).
        Firebase.initialize(
            options = if (useEmulators) {
                FirebaseOptions(
                    apiKey = "demo-key",
                    projectId = FirebaseProject.EMULATOR_ID,
                    applicationId = "demo-app",
                    // Popups (Continue with Google) need one; the emulator serves the page itself.
                    authDomain = "${FirebaseProject.EMULATOR_ID}.firebaseapp.com"
                )
            } else {
                FirebaseOptions(
                    apiKey = FirebaseProject.WEB_API_KEY,
                    authDomain = FirebaseProject.AUTH_DOMAIN,
                    databaseUrl = FirebaseProject.DATABASE_URL,
                    projectId = FirebaseProject.ID,
                    storageBucket = FirebaseProject.STORAGE_BUCKET,
                    applicationId = FirebaseProject.WEB_APP_ID,
                )
            }
        )

        // App Check in monitoring mode (SH-185), before Firestore starts: only with a site key in
        // the build and never against the emulators.
        if (!useEmulators) WebAppCheck.install(WebBuild.APP_CHECK_SITE_KEY)

        if (useEmulators) {
            val firestorePort = emulatorPort("firestorePort", EMULATOR_FIRESTORE_PORT)
            Firebase.auth.useEmulator(host, emulatorPort("authPort", EMULATOR_AUTH_PORT))
            // GitLive's JS wrapper applies settings lazily and useEmulator only takes effect when
            // settings exist. The app's one handle (AppFirestore) configures every instance it
            // starts this way, the first and the fresh one after a sign-out (SH-177).
            AppFirestore.configure = { firestore ->
                firestore.settings = firestoreSettings {
                    this.sslEnabled = false
                    this.host = "$host:$firestorePort"
                    // What the old setSettings(...) chose when persistence was not given.
                    this.cacheSettings = LocalCacheSettings.Persistent.newBuilder()
                        .apply { sizeBytes = FirebaseFirestoreSettings.CACHE_SIZE_UNLIMITED }
                        .build()
                }
                firestore.useEmulator(host, firestorePort)
            }
            FirebaseProject.useFunctionsEmulator(host, emulatorPort("functionsPort", EMULATOR_FUNCTIONS_PORT))
            // Profile photos (SH-94) go to the Storage emulator (firebase.json).
            FirebaseProject.useStorageEmulator(host, emulatorPort("storagePort", EMULATOR_STORAGE_PORT))
        }

        // An invitation link (/join?code=…, SH-42) waits in DeepLinks until the person is signed
        // in. The code leaves the address, so reloading the page does not open it again.
        if (DeepLinks.openUrl(window.location.href)) {
            val query = window.location.search.removePrefix("?").split('&')
                .filter { it.isNotBlank() && it.substringBefore('=') != "code" }
            val address = window.location.pathname + (if (query.isEmpty()) "" else "?" + query.joinToString("&"))
            window.history.replaceState(null, "", address)
        }

        ComposeViewport("ComposeTarget") {
            // The canvas renderer has no system fonts: wait for the bundled one (and the strings,
            // below), or the first frames show buttons and fields without any text.
            val font by preloadFont(Res.font.open_sans)
            // String resources also load asynchronously on the web; reading one fills the cache
            // of the whole strings file, so no screen starts with empty labels.
            var stringsReady by remember { mutableStateOf(false) }
            LaunchedEffect(Unit) {
                runCatching { getString(Res.string.cancel) }
                stringsReady = true
            }
            if (font != null && stringsReady) {
                App()
            } else {
                Box(Modifier.fillMaxSize().background(Color(0xFF001220)))
            }
        }
    }
}

/**
 * SH-208: a sideways wheel or two-finger trackpad swipe belongs to the app (the roster grid, the
 * tables), even where nothing scrolls further: the canvas only claims the events it used, so at a
 * grid's left edge Chrome on macOS took a swipe to the left as "back" and left the app. Cancelling
 * the browser's default for every sideways one keeps it in the page (Compose still receives the
 * event and scrolls). Up and down are untouched, and so is Ctrl + the wheel (zoom). The pages'
 * `overscroll-behavior: none` (app.html, availability-link.html) covers the same in CSS.
 */
private fun keepSidewaysWheelInTheApp() {
    val options: dynamic = js("({})")
    options.passive = false
    options.capture = true
    window.addEventListener("wheel", { event ->
        val wheel = event as? WheelEvent ?: return@addEventListener
        if (wheel.deltaX != 0.0 && !wheel.ctrlKey) wheel.preventDefault()
    }, options)
}

/** SH-173: where e-mailed availability links point (firebase.json rewrites everything under it to availability-link.html). */
private const val AVAILABILITY_LINK_PATH = "/d/"

/**
 * SH-173: the web form of an e-mailed availability link. It talks to two callable functions only
 * (availabilityLinkInfo, submitAvailabilityByLink), signed out; locally `?emulator` points them at
 * the Functions emulator (`&emulatorProject=demo-…` for another demo project).
 */
@OptIn(ExperimentalComposeUiApi::class, ExperimentalResourceApi::class)
private fun showAvailabilityLinkPage(token: String) {
    val host = window.location.hostname
    val params = URLSearchParams(window.location.search)
    if (params.has("emulator") && (host == "localhost" || host == "127.0.0.1")) {
        val project = params.get("emulatorProject")?.takeIf { it.startsWith("demo-") } ?: FirebaseProject.EMULATOR_ID
        FirebaseProject.useFunctionsEmulator(host, emulatorPort("functionsPort", EMULATOR_FUNCTIONS_PORT), project)
    }
    val functions = CloudFunctionsClient(HttpClient(), FirebaseProject.functionsBaseUrl) { null }
    val viewModel = AvailabilityLinkViewModel(token, AvailabilityLinkRepositoryImpl(functions))
    ComposeViewport("ComposeTarget") {
        val font by preloadFont(Res.font.open_sans)
        var stringsReady by remember { mutableStateOf(false) }
        LaunchedEffect(Unit) {
            runCatching { getString(Res.string.cancel) }
            stringsReady = true
        }
        if (font != null && stringsReady) {
            AppTheme { AvailabilityLinkScreen(viewModel) }
        } else {
            Box(Modifier.fillMaxSize().background(Color(0xFFFDFBF7)))
        }
    }
}
